/**
 * Utility functions for the Graphs feature
 * Provides data transformation, validation, and helper functions
 */

import moment from 'moment';
import { DEFAULT_NUMERIC_FIELDS, MAX_TIME_RANGE_HOURS } from './constants';

/**
 * Check if a field contains numeric data
 * @param {string} fieldKey - The field key to check
 * @param {Object} sampleData - Optional sample data point to verify
 * @returns {boolean} - True if the field contains numeric data
 */
export const isNumericField = (fieldKey, sampleData = null) => {
  // Check if field is in our known numeric fields
  const knownNumericField = DEFAULT_NUMERIC_FIELDS.find((f) => f.key === fieldKey);
  if (knownNumericField) {
    return true;
  }

  // If sample data provided, check if the value is numeric
  if (sampleData) {
    const value = getNestedValue(sampleData, fieldKey);
    return typeof value === 'number' && !isNaN(value);
  }

  // Check common numeric field patterns
  const numericPatterns = [
    /speed/i,
    /voltage/i,
    /fuel/i,
    /mileage/i,
    /altitude/i,
    /alt$/i,
    /temperature/i,
    /temp$/i,
    /battery/i,
    /level/i,
    /distance/i,
    /rpm/i,
    /pressure/i,
  ];

  return numericPatterns.some((pattern) => pattern.test(fieldKey));
};

/**
 * Get nested value from object using dot notation
 * @param {Object} obj - The object to extract value from
 * @param {string} path - Dot-notation path (e.g., 'velocity.speed')
 * @returns {*} - The value at the path or undefined
 */
export const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;

  // First try direct access (for flattened objects)
  if (obj[path] !== undefined) {
    return obj[path];
  }

  // Then try nested access
  const keys = path.split('.');
  let value = obj;

  for (const key of keys) {
    if (value === null || value === undefined) {
      return undefined;
    }
    value = value[key];
  }

  return value;
};

/**
 * Transform track points data to chart-compatible format
 * @param {Array} trackPoints - Array of track point objects
 * @param {Array} selectedFields - Array of field keys to include
 * @param {Array} fieldConfigs - Array of field configuration objects
 * @returns {Object} - Chart.js compatible data object
 */
export const transformTrackPointsToChartData = (
  trackPoints,
  selectedFields,
  fieldConfigs = DEFAULT_NUMERIC_FIELDS
) => {
  if (!trackPoints || !trackPoints.length || !selectedFields || !selectedFields.length) {
    return {
      labels: [],
      datasets: [],
    };
  }

  // Sort track points by source_time ascending
  const sortedPoints = [...trackPoints].sort((a, b) => {
    const timeA = a.source_time || a['source_time'] || 0;
    const timeB = b.source_time || b['source_time'] || 0;
    return timeA - timeB;
  });

  // Generate labels (timestamps)
  const labels = sortedPoints.map((point) => {
    const timestamp = point.source_time || point['source_time'];
    return moment(timestamp).format('HH:mm:ss');
  });

  // Generate datasets for each selected field
  const datasets = selectedFields.map((fieldKey) => {
    const fieldConfig = fieldConfigs.find((f) => f.key === fieldKey) || {
      key: fieldKey,
      label: fieldKey,
      color: '#6b7280',
      accessor: (p) => getNestedValue(p, fieldKey),
    };

    const data = sortedPoints.map((point, index) => {
      // Pass index and all points for fields that need context (like mileage delta)
      const value = fieldConfig.accessor(point, index, sortedPoints);
      return value != null && !isNaN(value) ? value : null;
    });

    return {
      label: `${fieldConfig.label}${fieldConfig.unit ? ` (${fieldConfig.unit})` : ''}`,
      data,
      borderColor: fieldConfig.color,
      backgroundColor: `${fieldConfig.color}20`,
      tension: 0.3,
      pointRadius: 2,
      pointHoverRadius: 6,
      fill: false,
      spanGaps: true,
    };
  });

  return {
    labels,
    datasets,
  };
};

/**
 * Validate time range does not exceed maximum allowed hours
 * @param {Object} range - Object with startDate and endDate (moment or Date objects)
 * @param {number} maxHours - Maximum allowed hours (default: 24)
 * @returns {Object} - { isValid: boolean, message: string, durationHours: number }
 */
export const validateTimeRange = (range, maxHours = MAX_TIME_RANGE_HOURS) => {
  if (!range || !range.startDate || !range.endDate) {
    return {
      isValid: false,
      message: 'Invalid time range: start and end dates are required',
      durationHours: 0,
    };
  }

  const start = moment(range.startDate);
  const end = moment(range.endDate);

  if (!start.isValid() || !end.isValid()) {
    return {
      isValid: false,
      message: 'Invalid time range: dates are not valid',
      durationHours: 0,
    };
  }

  if (end.isBefore(start)) {
    return {
      isValid: false,
      message: 'Invalid time range: end date must be after start date',
      durationHours: 0,
    };
  }

  const durationHours = end.diff(start, 'hours', true);

  if (durationHours > maxHours) {
    return {
      isValid: false,
      message: `Maximum time range is ${maxHours} hours`,
      durationHours,
    };
  }

  return {
    isValid: true,
    message: '',
    durationHours,
  };
};

/**
 * Get default 24-hour time range (last 24 hours from now)
 * @returns {Object} - { startDate: moment, endDate: moment }
 */
export const getDefault24HourRange = () => {
  return {
    startDate: moment().subtract(24, 'hours'),
    endDate: moment(),
  };
};

/**
 * Find the index of a track point in the data array
 * @param {Array} trackPoints - Array of track points
 * @param {Object} targetPoint - The track point to find
 * @returns {number} - Index of the point, or -1 if not found
 */
export const findTrackPointIndex = (trackPoints, targetPoint) => {
  if (!trackPoints || !targetPoint) return -1;

  const targetId = targetPoint.packet_id || targetPoint['packet_id'];
  const targetTime = targetPoint.source_time || targetPoint['source_time'];

  return trackPoints.findIndex((point) => {
    const pointId = point.packet_id || point['packet_id'];
    const pointTime = point.source_time || point['source_time'];

    // Match by packet_id if available
    if (targetId && pointId) {
      return pointId === targetId;
    }

    // Fall back to matching by source_time
    return pointTime === targetTime;
  });
};

/**
 * Filter fields to only include numeric fields
 * @param {Array} fields - Array of field objects with key property
 * @param {Array} sampleData - Optional sample data to verify field types
 * @returns {Array} - Filtered array of numeric fields only
 */
export const filterNumericFields = (fields, sampleData = null) => {
  if (!fields || !fields.length) return [];

  const sample = sampleData && sampleData.length > 0 ? sampleData[0] : null;

  return fields.filter((field) => {
    const fieldKey = typeof field === 'string' ? field : field.key;
    return isNumericField(fieldKey, sample);
  });
};
